import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";

/* =================================================================
   Prontuário Corporativo SST — Executive Summary A4
   Pdf "tudo em um" da empresa: header + KPIs + última avaliação +
   ações pendentes + timeline + status documental.
   CSS print-safe (height fixo, page-break controlado, sem cortes).
   ================================================================= */

const PRINT_STYLE_ID = "dossier-print-style";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString("pt-BR") : "—";

const DOC_TYPES = [
  { code: "pgr",    label: "PGR" },
  { code: "pcmso",  label: "PCMSO" },
  { code: "ltcat",  label: "LTCAT" },
  { code: "ppp",    label: "PPP" },
  { code: "aet",    label: "AET" },
  { code: "aso",    label: "ASO" },
  { code: "laudos", label: "Laudos" },
  { code: "trein",  label: "Treinamentos" },
];

/* Componentes auxiliares (fora do render para evitar re-mount) */
function PageHead({ companyId, issueDate }) {
  return (
    <div className="ds-head">
      <img src={LOGO_URL} alt="Sollute Ocupacional" />
      <div className="ds-meta">
        Prontuário <strong>#{(companyId || "").slice(0, 8).toUpperCase()}</strong><br/>
        Emitido em {issueDate}
      </div>
    </div>
  );
}
function PageFoot({ companyName, pageNum, totalPages }) {
  return (
    <div className="ds-foot">
      <div>{companyName} · Sollute Ocupacional</div>
      <div className="pg">Página <strong>{pageNum}</strong> / {totalPages}</div>
    </div>
  );
}

export default function CompanyDossier() {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [data, setData] = useState({ assessments: [], plans: [] });
  const [loading, setLoading] = useState(true);

  /* Injeta CSS print-only — ESPECIFICAMENTE para esta página.
     Cleanup ao desmontar pra não vazar pra outras telas. */
  useEffect(() => {
    let s = document.getElementById(PRINT_STYLE_ID);
    if (!s) {
      s = document.createElement("style");
      s.id = PRINT_STYLE_ID;
      document.head.appendChild(s);
    }
    s.textContent = `
      /* ========== Tela ========== */
      .dossier-stage{
        background: #E2E8F0;
        min-height: 100vh;
        padding: 28px 16px 64px;
        display: flex; flex-direction: column; align-items: center; gap: 18px;
      }
      .dossier-page{
        width: 210mm;
        min-height: 297mm;
        background: #fff;
        color: #1B2220;
        padding: 16mm 14mm;
        box-sizing: border-box;
        page-break-after: always;
        break-after: page;
        page-break-inside: avoid;
        position: relative;
        overflow: hidden;
        font-family: 'Inter', system-ui, sans-serif;
        font-size: 10pt;
        line-height: 1.5;
        box-shadow: 0 10px 32px rgba(15,59,102,.14);
      }
      .dossier-page:last-child{ page-break-after: auto; break-after: auto }
      .dossier-page .ds-head{
        display:flex; justify-content:space-between; align-items:center;
        border-bottom: 2px solid #00A859; padding-bottom: 8mm; margin-bottom: 8mm;
      }
      .dossier-page .ds-head img{ height: 38px; width: auto; object-fit: contain }
      .dossier-page .ds-head .ds-meta{
        text-align: right; font-size: 8pt; color:#6b6b6b; line-height: 1.5;
      }
      .dossier-page .ds-head .ds-meta strong{ color:#00A859; font-family:'JetBrains Mono', ui-monospace, monospace }
      .dossier-page h1{
        font-size: 18pt; font-weight: 800; letter-spacing: -.015em; color:#1a1a1a;
        margin: 0 0 4mm; line-height: 1.1;
      }
      .dossier-page h2{
        font-size: 12pt; font-weight: 700; color:#00A859; letter-spacing: -.005em;
        margin: 6mm 0 3mm; padding-bottom: 2mm; border-bottom: 1px solid #E0E5DF;
      }
      .dossier-page h3{
        font-size: 9pt; font-weight: 700; color:#373435; text-transform: uppercase;
        letter-spacing: .1em; margin: 4mm 0 2mm;
      }
      .ds-kpis{
        display: grid; grid-template-columns: repeat(4, 1fr); gap: 4mm; margin: 4mm 0 6mm;
      }
      .ds-kpi{
        border: 1px solid #E0E5DF; padding: 3mm 3mm; background:#F7FAF8;
        page-break-inside: avoid; break-inside: avoid;
      }
      .ds-kpi .lb{ font-size: 7pt; color:#5B6670; letter-spacing:.12em; text-transform: uppercase; font-weight:700 }
      .ds-kpi .vl{ font-size: 16pt; font-weight: 800; color:#1a1a1a; margin-top: 2mm; line-height: 1 }
      .ds-meta-grid{
        display:grid; grid-template-columns: 1fr 1fr; gap: 2mm 6mm; font-size: 9pt;
      }
      .ds-meta-grid .k{ color:#5B6670; font-size: 7pt; letter-spacing:.1em; text-transform:uppercase; font-weight: 700 }
      .ds-meta-grid .v{ color:#1B2220; font-weight:600; margin-bottom: 2mm }
      .ds-table{
        width: 100%; border-collapse: collapse; font-size: 9pt; margin: 3mm 0;
        page-break-inside: avoid; break-inside: avoid;
      }
      .ds-table th, .ds-table td{
        text-align: left; padding: 2mm 3mm; border-bottom: 1px solid #E0E5DF; vertical-align: top;
      }
      .ds-table th{
        background:#F7FAF8; font-size: 7.5pt; text-transform: uppercase; letter-spacing:.06em; color:#5B6670; font-weight:700;
      }
      .ds-empty{
        padding: 6mm; background:#F7FAF8; border: 1px dashed #BFC4C1; color:#5B6670;
        text-align: center; font-size: 9pt; font-style: italic;
      }
      .ds-timeline{ list-style: none; padding: 0; margin: 0 }
      .ds-timeline li{
        padding: 2mm 0 2mm 8mm; border-left: 2px solid #00A859; margin-left: 3mm;
        position: relative; page-break-inside: avoid; break-inside: avoid;
      }
      .ds-timeline li::before{
        content:''; position: absolute; left: -3.5px; top: 4mm;
        width: 5px; height: 5px; background: #00A859; border-radius: 50%;
      }
      .ds-timeline li .ts{ font-size: 7pt; color:#5B6670; font-family:'JetBrains Mono', monospace }
      .ds-timeline li .tt{ font-weight: 700; color:#1B2220; font-size: 9pt; margin-top: 1mm }
      .ds-timeline li .sb{ font-size: 8pt; color:#5B6670 }
      .ds-doc-grid{ display:grid; grid-template-columns: repeat(4, 1fr); gap: 3mm }
      .ds-doc{
        border: 1px solid #E0E5DF; padding: 3mm; font-size: 8pt;
        page-break-inside: avoid; break-inside: avoid;
      }
      .ds-doc .nm{ font-weight: 800; color:#1a1a1a; font-size: 9.5pt }
      .ds-doc .st{ color:#A37A00; font-size: 7pt; letter-spacing:.08em; text-transform: uppercase; font-weight: 700; margin-top: 1mm }
      .ds-foot{
        position: absolute; bottom: 8mm; left: 14mm; right: 14mm;
        display:flex; justify-content:space-between; align-items:center;
        font-size: 7pt; color:#8a8a8a; border-top: 1px solid #E0E5DF; padding-top: 2mm;
      }
      .ds-foot .pg strong{ color:#00A859; font-family:'JetBrains Mono', monospace }

      /* ========== Toolbar (no-print) ========== */
      .dossier-toolbar{
        position: sticky; top: 0; z-index: 5;
        display:flex; gap:10px; align-items:center;
        background: rgba(255,255,255,.96);
        backdrop-filter: blur(10px);
        border-bottom: 1px solid #E0E5DF;
        padding: 12px 20px; margin: -28px -16px 18px;
      }
      .dossier-toolbar h2{ font-size:.95rem; font-weight:650; margin:0; color:#1B2220 }
      .dossier-toolbar .sp{ flex:1 }

      /* ========== Print ========== */
      @page { size: A4 portrait; margin: 0; }
      @media print {
        body{ background:#fff !important; margin:0 !important; padding:0 !important }
        .no-print, .dossier-toolbar{ display: none !important }
        .dossier-stage{ background: #fff !important; padding: 0 !important; gap: 0 !important; min-height: 0 !important }
        .dossier-page{
          box-shadow: none !important;
          margin: 0 !important;
          page-break-after: always !important;
          break-after: page !important;
        }
        .dossier-page:last-child{ page-break-after: auto !important; break-after: auto !important }
        *{ -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important }
      }
    `;
    return () => {
      // Cleanup ao sair da rota — evita aplicar A4 às outras páginas
      const node = document.getElementById(PRINT_STYLE_ID);
      if (node) node.remove();
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: c }, aRes, pRes] = await Promise.all([
        api.get(`/companies/${id}`),
        api.get("/assessments").catch(() => ({ data: [] })),
        api.get("/action-plans").catch(() => ({ data: [] })),
      ]);
      setCompany(c);
      setData({
        assessments: (aRes.data || []).filter((a) => a.company_id === id),
        plans:       (pRes.data || []).filter((p) => p.company_id === id),
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading || !company) {
    return (
      <div className="p-12 text-center text-slate-500" data-testid="dossier-loading">
        <i className="fa-solid fa-spinner fa-spin text-3xl mb-3"></i>
        <p className="text-sm">Montando prontuário…</p>
      </div>
    );
  }

  /* ---------- Agregações ---------- */
  const lastAssessment = data.assessments
    .slice()
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0];
  const completedAssessments = data.assessments.filter((a) => typeof a.global_score === "number");
  const avgScore = completedAssessments.length
    ? completedAssessments.reduce((s, a) => s + a.global_score, 0) / completedAssessments.length
    : null;
  const pendingPlans = data.plans.filter((p) => p.status !== "concluido" && p.status !== "done");

  /* ---------- Timeline consolidada ---------- */
  const events = [];
  data.assessments.forEach((it) => {
    events.push({
      id: `a-${it.id}`,
      ts: it.created_at,
      title: `Avaliação criada: ${it.titulo}`,
      sub: `${(it.test_type || "nr01_54").toUpperCase()} · ${it.response_count ?? 0} respondente(s)`,
    });
    if (it.completed_at) events.push({
      id: `ac-${it.id}`,
      ts: it.completed_at,
      title: `Avaliação concluída: ${it.titulo}`,
      sub: `Score global ${it.global_score?.toFixed?.(2) || "—"}`,
    });
  });
  data.plans.forEach((it) => {
    events.push({
      id: `p-${it.id}`,
      ts: it.created_at,
      title: `Ação: ${it.title || it.descricao || "Plano de ação"}`,
      sub: `Status: ${it.status || "pendente"}`,
    });
  });
  events.sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0));
  const eventsTop = events.slice(0, 14);

  const totalPages = 3;
  const issueDate = new Date().toLocaleDateString("pt-BR");
  const cidadeUf = [company.cidade, company.uf].filter(Boolean).join("/");

  return (
    <div className="dossier-stage" data-testid="company-dossier">
      <div className="dossier-toolbar no-print">
        <h2><i className="fa-solid fa-folder-open mr-2 text-emerald-700"></i>Prontuário · {company.razao_social}</h2>
        <span className="sp"></span>
        <Link to={`/app/empresas/${id}`} className="btn btn-ghost text-xs" data-testid="dossier-back">
          <i className="fa-solid fa-arrow-left"></i> Voltar
        </Link>
        <button onClick={() => window.print()} className="btn btn-primary text-xs" data-testid="dossier-print">
          <i className="fa-solid fa-print"></i> Imprimir / Salvar PDF
        </button>
      </div>

      {/* ============ Página 1 — Capa & KPIs ============ */}
      <section className="dossier-page" data-testid="dossier-page-1">
        <PageHead companyId={company.id} issueDate={issueDate} />
        <div style={{ marginBottom: "8mm" }}>
          <div style={{ fontSize: "8pt", color: "#5B6670", textTransform: "uppercase", letterSpacing: ".15em", fontWeight: 700, marginBottom: "3mm" }}>
            Prontuário Corporativo SST
          </div>
          <h1>{company.razao_social}</h1>
          {company.nome_fantasia && (
            <div style={{ fontSize: "11pt", color: "#5B6670" }}>{company.nome_fantasia}</div>
          )}
        </div>

        <div className="ds-meta-grid">
          {company.cnpj           && <><div className="k">CNPJ</div><div className="v" style={{ fontFamily: "JetBrains Mono, monospace" }}>{company.cnpj}</div></>}
          {company.cnae           && <><div className="k">CNAE</div><div className="v" style={{ fontFamily: "JetBrains Mono, monospace" }}>{company.cnae}</div></>}
          {company.grau_risco     && <><div className="k">Grau de Risco</div><div className="v">{company.grau_risco}</div></>}
          {cidadeUf               && <><div className="k">Cidade / UF</div><div className="v">{cidadeUf}</div></>}
          {(company.n_trabalhadores || company.colaboradores) && <><div className="k">Colaboradores</div><div className="v">{company.n_trabalhadores || company.colaboradores}</div></>}
          {company.responsavel_tecnico && <><div className="k">Resp. Técnico</div><div className="v">{company.responsavel_tecnico}</div></>}
          {company.endereco       && <><div className="k">Endereço</div><div className="v">{company.endereco}</div></>}
          {company.status_contratual && <><div className="k">Status Contratual</div><div className="v">{company.status_contratual}</div></>}
        </div>

        <h2>Indicadores executivos</h2>
        <div className="ds-kpis">
          <div className="ds-kpi"><div className="lb">Colaboradores</div><div className="vl">{company.n_trabalhadores || company.colaboradores || 0}</div></div>
          <div className="ds-kpi"><div className="lb">Avaliações realizadas</div><div className="vl">{data.assessments.length}</div></div>
          <div className="ds-kpi"><div className="lb">Risco médio (1-5)</div><div className="vl">{avgScore != null ? avgScore.toFixed(2) : "—"}</div></div>
          <div className="ds-kpi"><div className="lb">Ações pendentes</div><div className="vl">{pendingPlans.length}</div></div>
          <div className="ds-kpi"><div className="lb">Avaliações concluídas</div><div className="vl">{completedAssessments.length}</div></div>
          <div className="ds-kpi"><div className="lb">Plano de ação total</div><div className="vl">{data.plans.length}</div></div>
          <div className="ds-kpi"><div className="lb">Conformidade NR-01</div><div className="vl">{avgScore != null ? `${Math.round(100 - Math.min(100, ((avgScore || 0) / 4) * 100))}%` : "—"}</div></div>
          <div className="ds-kpi"><div className="lb">Documentos no prontuário</div><div className="vl">0</div></div>
        </div>

        <h2>Documentos legais — status</h2>
        <div className="ds-doc-grid">
          {DOC_TYPES.map((d) => (
            <div key={d.code} className="ds-doc">
              <div className="nm">{d.label}</div>
              <div className="st">Não anexado</div>
            </div>
          ))}
        </div>

        <PageFoot companyName={company.razao_social} pageNum={1} totalPages={totalPages} />
      </section>

      {/* ============ Página 2 — Avaliações & Última campanha ============ */}
      <section className="dossier-page" data-testid="dossier-page-2">
        <PageHead companyId={company.id} issueDate={issueDate} />
        <h1 style={{ fontSize: "14pt", marginBottom: "4mm" }}>Avaliações Psicossociais</h1>

        {lastAssessment ? (
          <>
            <h3>Última campanha</h3>
            <div className="ds-meta-grid" style={{ marginBottom: "4mm" }}>
              <div className="k">Título</div><div className="v">{lastAssessment.titulo}</div>
              <div className="k">Instrumento</div><div className="v">{(lastAssessment.test_type || "nr01_54").toUpperCase()}</div>
              <div className="k">GHE / Setor</div><div className="v">{lastAssessment.ghe_setor || "—"}</div>
              <div className="k">Criada em</div><div className="v">{fmtDate(lastAssessment.created_at)}</div>
              <div className="k">Respondentes</div><div className="v">{lastAssessment.response_count ?? 0}</div>
              <div className="k">Score global</div><div className="v">{lastAssessment.global_score?.toFixed?.(2) || "—"}</div>
            </div>
          </>
        ) : (
          <div className="ds-empty">Nenhuma avaliação cadastrada para esta empresa ainda.</div>
        )}

        <h2>Todas as avaliações</h2>
        {data.assessments.length === 0 ? (
          <div className="ds-empty">Sem registros.</div>
        ) : (
          <table className="ds-table">
            <thead>
              <tr><th>Título</th><th>Instrumento</th><th>Setor</th><th>Criada</th><th style={{textAlign:'center'}}>Resp.</th><th style={{textAlign:'right'}}>Score</th></tr>
            </thead>
            <tbody>
              {data.assessments.slice(0, 18).map((a) => (
                <tr key={a.id}>
                  <td><strong>{a.titulo}</strong></td>
                  <td style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "8pt" }}>{(a.test_type || "nr01_54").toUpperCase()}</td>
                  <td>{a.ghe_setor || "—"}</td>
                  <td>{fmtDate(a.created_at)}</td>
                  <td style={{textAlign:'center'}}>{a.response_count ?? 0}</td>
                  <td style={{textAlign:'right', fontWeight: 700}}>{a.global_score?.toFixed?.(2) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data.assessments.length > 18 && (
          <div style={{ fontSize: "8pt", color: "#5B6670", marginTop: "2mm" }}>
            <em>… {data.assessments.length - 18} avaliação(ões) adicional(is). Veja no dashboard digital.</em>
          </div>
        )}

        <PageFoot companyName={company.razao_social} pageNum={2} totalPages={totalPages} />
      </section>

      {/* ============ Página 3 — Plano de Ação & Timeline ============ */}
      <section className="dossier-page" data-testid="dossier-page-3">
        <PageHead companyId={company.id} issueDate={issueDate} />
        <h1 style={{ fontSize: "14pt", marginBottom: "4mm" }}>Plano de Ação & Histórico</h1>

        <h2>Ações pendentes ({pendingPlans.length})</h2>
        {pendingPlans.length === 0 ? (
          <div className="ds-empty">Sem ações pendentes registradas.</div>
        ) : (
          <table className="ds-table">
            <thead>
              <tr><th>Ação</th><th>Responsável</th><th>Prazo</th><th>Status</th></tr>
            </thead>
            <tbody>
              {pendingPlans.slice(0, 12).map((p) => (
                <tr key={p.id}>
                  <td><strong>{p.title || p.descricao || "Ação"}</strong></td>
                  <td>{p.responsavel || p.owner || "—"}</td>
                  <td>{fmtDate(p.due_date || p.prazo)}</td>
                  <td><span style={{ color: "#A37A00", fontWeight: 700, fontSize: "8pt", textTransform: "uppercase" }}>{p.status || "pendente"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <h2>Timeline ({events.length} evento(s))</h2>
        {events.length === 0 ? (
          <div className="ds-empty">Sem histórico de eventos.</div>
        ) : (
          <ul className="ds-timeline">
            {eventsTop.map((ev) => (
              <li key={ev.id}>
                <div className="ts">{fmtDateTime(ev.ts)}</div>
                <div className="tt">{ev.title}</div>
                <div className="sb">{ev.sub}</div>
              </li>
            ))}
          </ul>
        )}
        {events.length > 14 && (
          <div style={{ fontSize: "8pt", color: "#5B6670", marginTop: "2mm" }}>
            <em>… mais {events.length - 14} evento(s) no histórico completo.</em>
          </div>
        )}

        <PageFoot companyName={company.razao_social} pageNum={3} totalPages={totalPages} />
      </section>
    </div>
  );
}
